import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import clsx from "clsx";
import { Copy, Check, Mail, Trash2 } from "lucide-react";
import Avatar from "../../../components/ui/Avatar";
import Modal from "../../../components/ui/Modal";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { getErrorMessage } from "../../../lib/apiClient";
import { canShareAsPdf } from "../../editor/editorApi";
import { billingKeys, getBillingUsage } from "../../billing/billingApi";
import {
  createShare,
  fileKeys,
  inviteByEmail,
  listPeopleWithAccess,
  revokeInvitation,
  updateInvitation,
} from "../filesApi";

const EXPIRY_OPTIONS = [
  { label: "Never", value: "" },
  { label: "1 hour", value: "3600" },
  { label: "1 day", value: "86400" },
  { label: "7 days", value: "604800" },
  { label: "30 days", value: "2592000" },
];

// UI access presets → backend (accessMode, allowDownload).
const ACCESS_OPTIONS = [
  { label: "View only", value: "view", allowDownload: false },
  { label: "View & download", value: "download", allowDownload: true },
  { label: "Edit", value: "edit", allowDownload: true },
];

const TABS = [
  { key: "link", label: "Link" },
  { key: "people", label: "People" },
];

export default function ShareModal({ open, onClose, target }) {
  const [tab, setTab] = useState("link");
  const usageQuery = useQuery({
    queryKey: billingKeys.usage,
    queryFn: getBillingUsage,
    enabled: open,
    retry: false,
  });
  const conversationsEnabled =
    usageQuery.data?.features?.fileConversations !== false;

  useEffect(() => {
    if (open) setTab("link");
  }, [open, target]);

  if (!target) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Share "${target.item.name}"`}
      footer={tab === "link" ? null : <Button onClick={onClose}>Done</Button>}
    >
      <div className="mb-4 flex border-b border-slate-200">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={clsx(
              "flex-1 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              tab === key
                ? "border-brand-500 text-slate-800"
                : "border-transparent text-slate-400 hover:text-slate-600",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "link" ? (
        <LinkTab
          target={target}
          onClose={onClose}
          conversationsEnabled={conversationsEnabled}
        />
      ) : (
        <PeopleTab
          target={target}
          conversationsEnabled={conversationsEnabled}
        />
      )}
    </Modal>
  );
}

function LinkTab({ target, onClose, conversationsEnabled }) {
  const [access, setAccess] = useState("download");
  const [password, setPassword] = useState("");
  const [expiresInSeconds, setExpiresInSeconds] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [allowChat, setAllowChat] = useState(false);
  const [sharePdf, setSharePdf] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);

  const pdfEligible = target.type === "file" && canShareAsPdf(target.item);

  const mutation = useMutation({
    mutationFn: () => {
      const preset = ACCESS_OPTIONS.find((o) => o.value === access);
      return createShare({
        resourceType: target.type,
        resourceId: target.item.id,
        accessMode: access,
        allowDownload: preset.allowDownload,
        allowChat,
        ...(sharePdf && pdfEligible ? { shareFormat: "pdf" } : {}),
        ...(password ? { password } : {}),
        ...(expiresInSeconds
          ? { expiresInSeconds: Number(expiresInSeconds) }
          : {}),
        ...(maxUses ? { maxUses: Number(maxUses) } : {}),
      });
    },
    onSuccess: (share) => {
      setLink(`${window.location.origin}/s/${share.token}`);
      toast.success("Share link created");
    },
    onError: (error) =>
      toast.error(getErrorMessage(error, "Could not create link")),
  });

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (link) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-slate-600">
            Anyone with this link can access the {target.type}:
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={link}
              className="flex-1 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm"
            />
            <Button variant="secondary" size="sm" onClick={copy}>
              {copied ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="block text-sm font-medium text-slate-700">
          Access
        </label>
        <Select
          value={access}
          onChange={(v) => setAccess(v)}
          options={ACCESS_OPTIONS}
          ariaLabel="Access"
          className="w-full"
        />
      </div>
      <Input
        label="Password (optional)"
        type="password"
        value={password}
        placeholder="Protect with a password"
        onChange={(e) => setPassword(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700">
            Expires
          </label>
          <Select
            value={expiresInSeconds}
            onChange={(v) => setExpiresInSeconds(v)}
            options={EXPIRY_OPTIONS}
            ariaLabel="Expires"
            className="w-full"
          />
        </div>
        <Input
          label="Max uses (optional)"
          type="number"
          min="1"
          value={maxUses}
          placeholder="Unlimited"
          onChange={(e) => setMaxUses(e.target.value)}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input
          type="checkbox"
          checked={allowChat}
          disabled={!conversationsEnabled}
          onChange={(e) => setAllowChat(e.target.checked)}
          className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        Allow chat &amp; comments (visitors must log in)
      </label>
      {!conversationsEnabled ? (
        <p className="-mt-2 text-xs text-amber-700">
          File comments and chat are not included in this workspace’s plan.
        </p>
      ) : null}
      {pdfEligible && (
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={sharePdf}
            onChange={(e) => setSharePdf(e.target.checked)}
            className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          Share as PDF (recipients preview &amp; download a PDF copy, not the
          original)
        </label>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
          Create link
        </Button>
      </div>
    </div>
  );
}

function PeopleTab({ target, conversationsEnabled }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [access, setAccess] = useState("view");
  const [allowChat, setAllowChat] = useState(false);
  const [sharePdf, setSharePdf] = useState(false);

  const pdfEligible = target.type === "file" && canShareAsPdf(target.item);

  const peopleQuery = useQuery({
    queryKey: fileKeys.invitations(target.type, target.item.id),
    queryFn: () => listPeopleWithAccess(target.type, target.item.id),
  });
  const people = peopleQuery.data ?? [];

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: fileKeys.invitations(target.type, target.item.id),
    });

  const inviteMutation = useMutation({
    mutationFn: () =>
      inviteByEmail({
        resourceType: target.type,
        resourceId: target.item.id,
        email: email.trim(),
        accessMode: access,
        allowChat,
        ...(sharePdf && pdfEligible ? { shareFormat: "pdf" } : {}),
      }),
    onSuccess: () => {
      setEmail("");
      setAllowChat(false);
      setSharePdf(false);
      invalidate();
      toast.success("Invitation sent");
    },
    onError: (err) =>
      toast.error(getErrorMessage(err, "Could not send invitation")),
  });

  const updateMutation = useMutation({
    mutationFn: updateInvitation,
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(getErrorMessage(err, "Could not update access")),
  });

  const revokeMutation = useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      invalidate();
      toast.success("Access revoked");
    },
    onError: (err) =>
      toast.error(getErrorMessage(err, "Could not revoke access")),
  });

  return (
    <div className="space-y-4">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) inviteMutation.mutate();
        }}
      >
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <Input
              label="Email"
              type="email"
              placeholder="colleague@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Select
            value={access}
            onChange={(v) => setAccess(v)}
            options={ACCESS_OPTIONS}
            ariaLabel="Access level"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={allowChat}
            disabled={!conversationsEnabled}
            onChange={(e) => setAllowChat(e.target.checked)}
            className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          Allow chat &amp; comments
        </label>
        {!conversationsEnabled ? (
          <p className="-mt-1 text-xs text-amber-700">
            Not included in this workspace’s plan.
          </p>
        ) : null}
        {pdfEligible && (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={sharePdf}
              onChange={(e) => setSharePdf(e.target.checked)}
              className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Share as PDF (they preview &amp; download a PDF copy, not the
            original)
          </label>
        )}
        <Button
          type="submit"
          loading={inviteMutation.isPending}
          disabled={!email.trim()}
        >
          <Mail className="h-4 w-4" />
          Invite
        </Button>
      </form>

      {people.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <p className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            People with access
          </p>
          {people.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 border-b border-slate-100 px-3 py-2.5 last:border-b-0"
            >
              <Avatar
                name={p.displayName ?? p.email}
                className="h-8 w-8 bg-slate-200 text-xs font-semibold text-slate-600"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-700">
                  {p.displayName ?? p.email}
                </p>
                <p className="truncate text-xs text-slate-400">
                  {p.email} ·{" "}
                  {p.status === "pending"
                    ? "invited (no account yet)"
                    : "has access"}
                </p>
              </div>
              <Select
                value={p.accessMode}
                onChange={(v) =>
                  updateMutation.mutate({ id: p.id, accessMode: v })
                }
                options={ACCESS_OPTIONS}
                size="sm"
                ariaLabel={`Access for ${p.email}`}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => revokeMutation.mutate(p.id)}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
